import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError, setAuthHeaderProvider, uploadFile } from '../api/client'
import { config, isEntraConfigured } from '../config'
import type { LoginResponse, TwoFactorSetup, User } from '../api/types'
import { prepareProfilePhoto } from '../lib/imageResize'
import { fetchMicrosoftProfilePhoto } from './graphPhoto'
import { loginRequest, msalInstance } from './msal'
import { StepUpDialog } from './StepUpDialog'
import { LOCAL_TOKEN_KEY, readLocalToken, userAuthHeaders } from './userAuthHeaders'

/** Markerer at vi har forsøgt at hente profilbilledet fra Microsoft 365 for en bruger. */
const PHOTO_SYNC_KEY = 'tcpss.photoSyncAttempted'

/**
 * Hvor langt et lokalt login er nået (beslutning 035).
 * - `done`: brugeren er inde
 * - `code`: kontoen har to-faktor, der mangler en kode fra appen
 * - `enrol`: rollen kræver to-faktor, og brugeren har aldrig sat det op
 */
export type LoginStep =
  | { kind: 'done' }
  | { kind: 'code'; challenge: string }
  | { kind: 'enrol'; challenge: string }

interface AuthState {
  /** Den lokale brugerprofil inkl. roller. Null indtil brugeren er logget ind. */
  user: User | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
  /** Microsoft 365-login (redirect til Entra ID). */
  login: () => Promise<void>
  /**
   * Login med e-mail og adgangskode for lokale brugere. Kaster ved fejl.
   * Returnerer hvilket trin der mangler - se <see cref="LoginStep"/>.
   */
  loginLocal: (email: string, password: string) => Promise<LoginStep>
  /** Andet trin: koden fra authenticator-appen eller en genoprettelseskode. */
  completeTwoFactor: (challenge: string, code: string) => Promise<void>
  /** Starter den tvungne tilmelding under login og henter hemmeligheden. */
  beginLoginEnrolment: (challenge: string) => Promise<TwoFactorSetup>
  /**
   * Afslutter tilmeldingen og returnerer genoprettelseskoderne sammen med en
   * `finish`, der gennemfører selve login. Koderne skal vises, før den kaldes -
   * de kan aldrig hentes igen.
   */
  completeLoginEnrolment: (
    challenge: string,
    code: string
  ) => Promise<{ codes: string[]; finish: () => void }>
  logout: () => Promise<void>
  /** Henter profilen igen, f.eks. efter skift af adgangskode. */
  refreshProfile: () => Promise<void>
  hasRole: (...roles: string[]) => boolean
  isAdmin: boolean
  /** Projektleder, Kontor og op (beslutning 108). */
  isProjectManager: boolean
  /** Må styre intranettet: Administrator, Superadmin og Kontor (beslutning 108). */
  isIntranetAdmin: boolean
  /** Må have en VPN-profil som montør - alle undtagen lærlinge (beslutning 108). */
  canUseVpn: boolean
  /** Kun Superadmin - fx til at slette sager permanent (beslutning 076). */
  isSuperadmin: boolean
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * Håndterer login og henter brugerens profil fra vores eget API.
 *
 * To loginmetoder lever side om side (docs/DECISIONS.md beslutning 013):
 * - Microsoft 365 via MSAL (redirect-flow, token fra Entra ID)
 * - lokal bruger via POST /api/auth/login (token udstedt af API-et, gemt i localStorage)
 *
 * Rollerne kommer i begge tilfælde fra API-et, ikke fra tokenet, så rettigheder er ens
 * på alle platforme og en rolleændring slår igennem uden nyt login.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Kobler API-klienten til den aktuelle login-metode. Samme regel som
  // TV-forhåndsvisningen - den står ét sted (beslutning 087).
  useEffect(() => {
    setAuthHeaderProvider(() => userAuthHeaders({ redirectOnInteraction: true }))
  }, [])

  const loadProfile = useCallback(async () => {
    try {
      setError(null)
      const profile = await api.get<User>('/api/me')
      setUser(profile)
    } catch (err) {
      setUser(null)
      if (err instanceof ApiError && err.status === 401 && readLocalToken()) {
        // Udløbet eller ugyldigt lokalt token - ryd det, så login-siden vises.
        localStorage.removeItem(LOCAL_TOKEN_KEY)
      } else {
        setError(err instanceof Error ? err.message : 'Kunne ikke hente brugerprofilen.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function initialise() {
      if (readLocalToken()) {
        await loadProfile()
        return
      }

      if (!isEntraConfigured) {
        if (config.devUser) {
          await loadProfile()
        } else {
          setIsLoading(false)
        }
        return
      }

      try {
        await msalInstance!.initialize()
        const redirectResult = await msalInstance!.handleRedirectPromise()

        if (redirectResult?.account) {
          msalInstance!.setActiveAccount(redirectResult.account)
        } else {
          const existing = msalInstance!.getAllAccounts()[0]
          if (existing) {
            msalInstance!.setActiveAccount(existing)
          }
        }

        if (cancelled) return

        if (msalInstance!.getActiveAccount()) {
          await loadProfile()
        } else {
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Login mislykkedes.')
          setIsLoading(false)
        }
      }
    }

    void initialise()
    return () => {
      cancelled = true
    }
  }, [loadProfile])

  // Rollerne hentes igen i stilhed, når appen kommer frem på skærmen, og hvert 5. minut - ellers ser en bruger,
  // der lige har fået en ny rolle (fx Kontor), ikke de nye knapper, før appen lukkes helt (Simon 2026-10-01).
  // En fejl her logger ALDRIG ud: et tabt signal i en kælder må ikke smide montøren af.
  const isLoggedIn = user !== null
  useEffect(() => {
    if (!isLoggedIn) return

    let busy = false
    async function refresh() {
      if (busy || document.visibilityState !== 'visible') return
      busy = true
      try {
        const profile = await api.get<User>('/api/me')
        setUser((current) => (current && JSON.stringify(current) === JSON.stringify(profile) ? current : profile))
      } catch {
        // Stille - næste forsøg kommer af sig selv.
      } finally {
        busy = false
      }
    }

    const onVisible = () => void refresh()
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(() => void refresh(), 5 * 60 * 1000)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [isLoggedIn])

  // Første gang en Microsoft 365-bruger logger ind uden profilbillede, hentes billedet
  // fra Graph og gemmes i systemet. Forsøget markeres i localStorage, så brugere uden
  // billede i 365 ikke rammer Graph ved hvert eneste login.
  useEffect(() => {
    if (!user || user.authProvider !== 'entra' || user.photoUrl) {
      return
    }

    const attemptKey = `${PHOTO_SYNC_KEY}.${user.id}`
    try {
      if (localStorage.getItem(attemptKey)) return
      localStorage.setItem(attemptKey, new Date().toISOString())
    } catch {
      return
    }

    let cancelled = false

    void (async () => {
      const blob = await fetchMicrosoftProfilePhoto()
      if (!blob || cancelled) return

      try {
        const prepared = await prepareProfilePhoto(blob)
        await uploadFile(`/api/users/${user.id}/photo?source=microsoft365`, prepared, 'profile.jpg')
        if (!cancelled) await loadProfile()
      } catch {
        // Billedet er ikke kritisk - brugeren kan altid uploade et manuelt.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user, loadProfile])

  const login = useCallback(async () => {
    if (!isEntraConfigured || !msalInstance) {
      setError('Microsoft-login er ikke konfigureret. Sæt VITE_ENTRA_CLIENT_ID og VITE_ENTRA_TENANT_ID.')
      return
    }
    await msalInstance.loginRedirect(loginRequest)
  }, [])

  /** Gemmer tokenet og profilen fra et gennemført login. */
  const acceptLogin = useCallback((result: LoginResponse) => {
    if (!result.token || !result.user) {
      throw new Error('Login mislykkedes.')
    }
    localStorage.setItem(LOCAL_TOKEN_KEY, result.token)
    setUser(result.user)
    setIsLoading(false)
  }, [])

  const loginLocal = useCallback(
    async (email: string, password: string): Promise<LoginStep> => {
      setError(null)
      const result = await api.post<LoginResponse>('/api/auth/login', { email, password })

      // Kontoen har to-faktor - eller skal have det. Ingen af delene giver et
      // token her; det kommer først, når koden er givet.
      if (result.challenge) {
        return { kind: result.twoFactorSetupRequired ? 'enrol' : 'code', challenge: result.challenge }
      }

      acceptLogin(result)
      return { kind: 'done' }
    },
    [acceptLogin]
  )

  const completeTwoFactor = useCallback(
    async (challenge: string, code: string) => {
      setError(null)
      acceptLogin(await api.post<LoginResponse>('/api/auth/login/two-factor', { challenge, code }))
    },
    [acceptLogin]
  )

  const beginLoginEnrolment = useCallback(
    (challenge: string) => api.post<TwoFactorSetup>('/api/auth/login/two-factor/setup', { challenge }),
    []
  )

  const completeLoginEnrolment = useCallback(
    async (challenge: string, code: string) => {
      setError(null)
      const result = await api.post<LoginResponse>('/api/auth/login/two-factor/enrol', { challenge, code })

      // Login gennemføres ikke her: så snart brugeren er logget ind, forlader
      // routeren login-siden, og genoprettelseskoderne ville forsvinde med den.
      // De skal ses først - de kan aldrig hentes igen.
      return {
        codes: result.recoveryCodes ?? [],
        finish: () => acceptLogin(result)
      }
    },
    [acceptLogin]
  )

  const logout = useCallback(async () => {
    const wasLocal = Boolean(readLocalToken())
    localStorage.removeItem(LOCAL_TOKEN_KEY)
    setUser(null)

    if (!wasLocal && msalInstance?.getActiveAccount()) {
      await msalInstance.logoutRedirect()
    }
  }, [])

  const value = useMemo<AuthState>(() => {
    const roles = user?.roles ?? []
    const hasRole = (...wanted: string[]) => wanted.some((role) => roles.includes(role))

    return {
      user,
      isLoading,
      error,
      isAuthenticated: user !== null,
      login,
      loginLocal,
      completeTwoFactor,
      beginLoginEnrolment,
      completeLoginEnrolment,
      logout,
      refreshProfile: loadProfile,
      hasRole,
      isAdmin: hasRole('Superadmin', 'Administrator'),
      isProjectManager: hasRole('Superadmin', 'Administrator', 'Kontor', 'Projektleder'),
      isIntranetAdmin: hasRole('Superadmin', 'Administrator', 'Kontor'),
      canUseVpn: hasRole('Superadmin', 'Administrator', 'Kontor', 'Projektleder', 'Medarbejder'),
      isSuperadmin: hasRole('Superadmin')
    }
  }, [
    user,
    isLoading,
    error,
    login,
    loginLocal,
    completeTwoFactor,
    beginLoginEnrolment,
    completeLoginEnrolment,
    logout,
    loadProfile
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Beder om sikkerhedsnøgle, når API'et kræver det (beslutning 092). */}
      <StepUpDialog />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth skal bruges inde i en AuthProvider.')
  }
  return context
}
